import React from 'react';
import { Link } from 'react-router-dom';
import { FiShoppingCart, FiStar, FiEye } from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { formatPrice, getImageUrl, truncate } from '../../utils/helpers';
import toast from 'react-hot-toast';

const ProductCard = ({ product }) => {
  const { addToCart } = useCart();

  const handleAddToCart = (e) => {
    e.preventDefault();
    addToCart(product);
    toast.success(`Added to cart!`, { duration: 1800, icon: '🛒' });
  };

  const discount = product.comparePrice && product.comparePrice > product.price
    ? Math.round(((product.comparePrice - product.price) / product.comparePrice) * 100)
    : 0;

  return (
    <Link
      to={`/product/${product.slug || product._id}`}
      className="group card flex flex-col overflow-hidden rounded-2xl border border-transparent hover:border-primary-100 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative overflow-hidden bg-gray-50" style={{ paddingBottom: '72%' }}>
        <img
          src={getImageUrl(product.images?.[0])}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-400"
          onError={(e) => { e.target.src = '/placeholder.png'; }}
        />

        {/* Badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1">
          {discount > 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              -{discount}%
            </span>
          )}
          {product.onSale && discount === 0 && (
            <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              SALE
            </span>
          )}
          {product.isFeatured && (
            <span className="bg-gradient-to-r from-yellow-400 to-orange-400 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              Featured
            </span>
          )}
          {product.stock === 0 && (
            <span className="bg-gray-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              Out of Stock
            </span>
          )}
        </div>

        {/* Quick action overlay */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-all duration-300 flex items-end justify-center pb-3 opacity-0 group-hover:opacity-100">
          <span className="flex items-center gap-1.5 bg-white/90 backdrop-blur-sm text-primary-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-sm">
            <FiEye size={12} /> Quick View
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        {product.category?.name && (
          <p className="text-[11px] text-primary-500 font-semibold uppercase tracking-wide mb-1">{product.category.name}</p>
        )}
        <h3 className="font-semibold text-gray-800 text-sm leading-snug mb-2 flex-1 line-clamp-2">
          {product.name}
        </h3>

        {product.brand && (
          <p className="text-xs text-gray-400 mb-2">{product.brand}</p>
        )}

        {product.numReviews > 0 && (
          <div className="flex items-center gap-1 mb-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <FiStar
                  key={i}
                  size={11}
                  className={i < Math.round(product.rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-200 fill-gray-200'}
                />
              ))}
            </div>
            <span className="text-[10px] text-gray-400">({product.numReviews})</span>
          </div>
        )}

        {/* Price row */}
        <div className="flex items-center justify-between mt-auto pt-2 border-t border-gray-50">
          <div>
            <span className="font-extrabold text-primary-600 text-base">{formatPrice(product.price)}</span>
            {product.comparePrice > product.price && (
              <span className="text-gray-400 text-xs line-through ml-1.5">{formatPrice(product.comparePrice)}</span>
            )}
          </div>
          {product.stock > 0 ? (
            <button
              onClick={handleAddToCart}
              className="flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 active:scale-95 text-white text-xs font-bold px-3 py-2 rounded-xl transition-all duration-150 shadow-sm hover:shadow-md"
            >
              <FiShoppingCart size={13} />
              <span className="hidden sm:inline">Add</span>
            </button>
          ) : (
            <span className="text-xs text-gray-400 font-medium">Unavailable</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
